/**
 * lucide-react → Material Symbols Outlined shim barrel.
 *
 * Existing pages import named icons like:
 *   import { RefreshCwIcon, Trash2Icon, XIcon } from 'lucide-react';
 *   <RefreshCwIcon className="w-4 h-4" />
 *
 * We swap the import path to this barrel:
 *   import { RefreshCwIcon, Trash2Icon, XIcon } from '../components/icons.js';
 *
 * Each export is a `<span class="material-symbols-outlined">` wrapper that
 * accepts `className` (so existing `w-4 h-4 text-brand-700` etc. keep working)
 * and renders the matching Material Symbol. The Tailwind size classes are
 * read by the wrapper and translated to a font-size, so glyphs render at the
 * intended px size even though they're text not SVG.
 *
 * Add a new mapping here whenever a new lucide icon is introduced in TSX —
 * keep it sorted alphabetically by Material Symbol name to make merges
 * painless.
 */
import type { ReactElement } from 'react';
import { lucideShim } from './Icon.js';
import type { LucideShimProps } from './Icon.js';

export type LucideIcon = (props: LucideShimProps) => ReactElement;

// ── Status / actions ──────────────────────────────────────────────
export const AlertCircleIcon = lucideShim('error');
export const AlertTriangleIcon = lucideShim('warning');
export const ArrowForwardIcon = lucideShim('arrow_forward');
export const ArrowUpDownIcon = lucideShim('unfold_more');
export const AttachFileIcon = lucideShim('attach_file');
export const BanIcon = lucideShim('block');
export const BrainIcon = lucideShim('psychology');
export const CableIcon = lucideShim('cable');
export const UserIcon = lucideShim('person');
export const BookTextIcon = lucideShim('menu_book');
export const CheckIcon = lucideShim('check');
export const CheckCircle2Icon = lucideShim('check_circle');
export const ChevronDownIcon = lucideShim('expand_more');
export const ChevronRightIcon = lucideShim('chevron_right');
export const ChevronLeftIcon = lucideShim('chevron_left');
export const ChevronUpIcon = lucideShim('expand_less');
export const CodeIcon = lucideShim('code');
export const CopyIcon = lucideShim('content_copy');
export const ExternalLinkIcon = lucideShim('open_in_new');
export const FileIcon = lucideShim('draft');
export const FlagIcon = lucideShim('flag');
export const FlagFilledIcon = lucideShim('flag_2');
export const FilesIcon = lucideShim('folder_copy');
export const VideoIcon = lucideShim('movie');
export const PlayIcon = lucideShim('play_arrow');
export const FileTextIcon = lucideShim('description');
export const PresentationIcon = lucideShim('slideshow');
export const GitBranchIcon = lucideShim('device_hub');
export const HistoryIcon = lucideShim('history');
export const ImageIcon = lucideShim('image');
export const LinkIcon = lucideShim('link');
export const LayoutGridIcon = lucideShim('grid_view');
export const ListIcon = lucideShim('list');
export const DownloadIcon = lucideShim('download');
export const Loader2Icon = lucideShim('progress_activity');
export const LogOutIcon = lucideShim('logout');
export const MailIcon = lucideShim('mail');
export const MenuIcon = lucideShim('menu');
export const MessageSquareTextIcon = lucideShim('forum');
export const MinusIcon = lucideShim('remove');
export const PencilIcon = lucideShim('edit');
export const PlusIcon = lucideShim('add');
export const SendIcon = lucideShim('send');
export const RefreshCwIcon = lucideShim('sync');
export const SearchIcon = lucideShim('search');
export const SendHorizontalIcon = lucideShim('send');
export const SettingsIcon = lucideShim('settings');
export const StopCircleIcon = lucideShim('stop_circle');
export const TableIcon = lucideShim('table_chart');
export const TagIcon = lucideShim('sell');
export const TicketIcon = lucideShim('confirmation_number');
export const TuneIcon = lucideShim('tune');
export const ThumbsDownIcon = lucideShim('thumb_down');
export const ThumbsUpIcon = lucideShim('thumb_up');
export const Trash2Icon = lucideShim('delete');
export const UploadCloudIcon = lucideShim('cloud_upload');
export const WandIcon = lucideShim('auto_fix_high');
export const XIcon = lucideShim('close');

// ── Training Videos surface ───────────────────────────────────────
export const PlayCircleIcon = lucideShim('play_circle');
export const PauseIcon = lucideShim('pause');
export const Replay10Icon = lucideShim('replay_10');
export const Forward10Icon = lucideShim('forward_10');
export const SubtitlesIcon = lucideShim('subtitles');
export const SubtitlesOffIcon = lucideShim('subtitles_off');
export const SpeedIcon = lucideShim('speed');
export const FullscreenIcon = lucideShim('fullscreen');
export const PlaylistPlayIcon = lucideShim('playlist_play');
export const FormatListBulletedIcon = lucideShim('format_list_bulleted');
export const FolderOpenIcon = lucideShim('folder_open');
export const FolderOffIcon = lucideShim('folder_off');
export const VideoLibraryIcon = lucideShim('video_library');
export const AllInclusiveIcon = lucideShim('all_inclusive');
export const BookmarkIcon = lucideShim('bookmark');
export const BookmarkBorderIcon = lucideShim('bookmark_border');
export const IosShareIcon = lucideShim('ios_share');
export const MoreHorizIcon = lucideShim('more_horiz');
export const ScheduleIcon = lucideShim('schedule');
export const VisibilityIcon = lucideShim('visibility');
export const EventIcon = lucideShim('event');
export const GraphicEqIcon = lucideShim('graphic_eq');
export const NorthEastIcon = lucideShim('north_east');
export const ChatBubbleOutlineIcon = lucideShim('chat_bubble_outline');
export const ArrowUpwardIcon = lucideShim('arrow_upward');
export const ArrowBackIcon = lucideShim('arrow_back');
export const PolicyIcon = lucideShim('policy');
export const PendingIcon = lucideShim('pending');
export const InfoIcon = lucideShim('info');
export const SearchOffIcon = lucideShim('search_off');
export const AutoAwesomeIcon = lucideShim('auto_awesome');
